import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SourcingCategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/sourcing-categories")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!confirm(`Remove "${c.name}" from sourcing categories? Ideas Gemini generates in this category will no longer show as Sourceable.`)) return;
    try {
      await api.delete(`/admin/sourcing-categories/${c.id}`);
      toast.success("Removed from sourcing");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="sourcing-categories-admin">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-3 py-1 mb-2">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.25} />
            <span className="text-[10px] uppercase tracking-widest font-bold">Vendor fulfilment whitelist</span>
          </div>
          <p className="text-sm text-charcoal/70">
            Anything Gemini suggests that <b>isn't in inventory</b> is checked against this list.
            If the category matches, the item shows as <span className="text-violet-700 font-semibold">Sourceable · Can be arranged</span>.
            If it doesn't match, it's silently hidden from the customer and logged for admin review.
          </p>
          <p className="text-xs text-charcoal/50 mt-2">
            Gemini stays fully creative — this list only gates the post-generation check, never the prompt.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowDialog(true); }}
          className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
          data-testid="add-sourcing-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2.25} /> Add category
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-6 text-charcoal/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-charcoal/60" data-testid="sourcing-empty">
            No sourcing categories yet. Add the categories your vendors can fulfil.
          </div>
        ) : (
          <div className="divide-y divide-warm">
            {items.map((c, i) => (
              <div key={c.id} className="p-4 flex items-center gap-4" data-testid={`sourcing-row-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-violet-600" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg text-charcoal" data-testid={`sourcing-name-${i}`}>{c.name}</p>
                  <p className="text-xs text-charcoal/50 font-mono">{c.slug}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditing(c); setShowDialog(true); }}
                    className="p-2 rounded-full hover:bg-linen-2"
                    data-testid={`sourcing-edit-${i}`}
                  >
                    <Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="p-2 rounded-full hover:bg-rose-50"
                    data-testid={`sourcing-delete-${i}`}
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDialog && (
        <SourcingDialog
          initial={editing}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function SourcingDialog({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      if (initial) await api.patch(`/admin/sourcing-categories/${initial.id}`, payload);
      else await api.post("/admin/sourcing-categories", payload);
      toast.success(initial ? "Updated" : "Added to sourcing");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
      onClick={onClose} data-testid="sourcing-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-md w-full shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Sourcing</p>
            <p className="font-display text-2xl text-charcoal mt-1">
              {initial ? "Edit category" : "Add sourcing category"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2" data-testid="sourcing-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
              Category name
            </label>
            <input
              required autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              placeholder="e.g. Balloons"
              data-testid="sourcing-name"
            />
            <p className="text-xs text-charcoal/50 mt-2">
              Slug is auto-generated from the name (e.g., "Gift Hampers" → <code className="bg-linen-2 px-1 rounded">gift-hampers</code>).
            </p>
          </div>
        </div>
        <footer className="p-6 border-t border-warm flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="rounded-full px-5 py-3 text-sm font-bold text-charcoal bg-white border border-warm hover:border-charcoal transition"
            data-testid="sourcing-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="sourcing-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </form>
    </div>
  );
}
